/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import { useEffect, useRef } from 'react';
import { useAuth } from '@/lib/auth';
import { IP_CONSOLE_BANNER, isProtectedSession } from '@/config/ipProtection';

const BODY_CLASS = 'sn-protected';
const DEBUGGER_INTERVAL_MS = 1500;

const isEditable = (el) => {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable === true;
};

/** Keyboard combos that open DevTools / view-source / save page. */
const isBlockedShortcut = (e) => {
  const key = String(e.key || '').toUpperCase();
  if (key === 'F12') return true;
  const mod = e.ctrlKey || e.metaKey;
  if (mod && e.shiftKey && (key === 'I' || key === 'J' || key === 'C' || key === 'K')) return true;
  if (mod && (key === 'U' || key === 'S')) return true;
  return false;
};

/**
 * AntiTamperGuard — role-based front-end protection.
 *
 * ENGAGED when the session is protected (unauthenticated visitor, patient,
 * demo or guest accounts):
 *   - right-click / context menu disabled
 *   - F12, Ctrl/Cmd+Shift+I/J/C/K, Ctrl/Cmd+U, Ctrl/Cmd+S intercepted
 *   - periodic `debugger` statement (only bites when DevTools is open)
 *   - `body.sn-protected` → user-select: none (form fields stay editable)
 *
 * BYPASSED entirely for the Root Administrator (`is_admin`), who keeps full
 * DevTools, right-click and text selection for development/debugging.
 *
 * The guard is reactive: it re-evaluates whenever the auth state changes, so
 * logging out of an admin account (or a session expiring → user === null)
 * re-engages the protections instantly on the login screen.
 */
export const AntiTamperGuard = () => {
  const { user, loading } = useAuth();
  const bannerShown = useRef(false);

  useEffect(() => {
    if (bannerShown.current) return;
    bannerShown.current = true;
    try {
       
      console.info(IP_CONSOLE_BANNER, 'font-weight:700;font-size:14px;color:#0F766E', 'font-weight:400;color:inherit');
    } catch { /* noop */ }
  }, []);

  // While the session is still resolving we stay protected (fail-closed).
  const engaged = loading ? true : isProtectedSession(user);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const body = document.body;
    body.dataset.snGuard = engaged ? 'on' : 'off';

    if (!engaged) {
      body.classList.remove(BODY_CLASS);
      return undefined;
    }

    body.classList.add(BODY_CLASS);

    const onContextMenu = (e) => {
      if (isEditable(e.target)) return; // keep native menu inside form fields (paste, spellcheck)
      e.preventDefault();
    };
    const onKeyDown = (e) => {
      if (isBlockedShortcut(e)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const onDragStart = (e) => {
      if (e.target && e.target.tagName === 'IMG') e.preventDefault();
    };

    document.addEventListener('contextmenu', onContextMenu, true);
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('dragstart', onDragStart, true);

    const timer = window.setInterval(() => {
      try {
         
        (function snGuardTick() { debugger; })();
      } catch { /* noop */ }
    }, DEBUGGER_INTERVAL_MS);

    return () => {
      document.removeEventListener('contextmenu', onContextMenu, true);
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('dragstart', onDragStart, true);
      window.clearInterval(timer);
      body.classList.remove(BODY_CLASS);
    };
  }, [engaged]);

  return null;
};

export default AntiTamperGuard;
